/** @jsxImportSource @emotion/react */
'use memo'

import React, { useState } from 'react'
import { useFormikContext } from 'formik'
import type { EditPlayerFormValues } from '@sportninja/common/features/profile/player'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import useWindowSize from 'src/hooks/useWindowSize'
import { FullScreenHeader } from 'src/components/FullScreenOverlay'
import { ScrollSpyLayout } from 'src/components/ScrollSpyLayout'
import SalesforceIdChip from 'src/features/team/player/components/SalesforceIdChip'
import { useEditPlayerContext } from './EditPlayerContext'
import { PLAYER_SETUP_ITEMS } from './playerSetupItems'
import HeaderCancelIcon from './components/HeaderCancelIcon'
import HeaderSaveIcon from './components/HeaderSaveIcon'
import PlayerFormSkeleton from './components/PlayerFormSkeleton'
import PersonalSection from './components/sections/PersonalSection'
import AddressSection from './components/sections/AddressSection'
import EmergencyContactSection from './components/sections/EmergencyContactSection'
import SocialProfilesSection from './components/sections/SocialProfilesSection'
import MembershipSection from './components/sections/MembershipSection'
import ConfirmDiscardChangesModal from './components/ConfirmDiscardChangesModal'

const EditPlayerModalContent: React.FC = () => {
  const { width } = useWindowSize()
  const isMobile = width <= 768

  const { isLoading, onCancel, salesforceId } = useEditPlayerContext()
  const { submitForm, dirty } = useFormikContext<EditPlayerFormValues>()

  const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = useState(false)

  const handleSave = async () => {
    await submitForm()
  }

  // Cancel/Back guard: warn before discarding edits, but close straight away
  // when the form is untouched.
  const handleRequestClose = () => {
    if (dirty) {
      setIsConfirmDiscardOpen(true)
    } else {
      onCancel()
    }
  }

  return (
    <>
      <FullScreenHeader
        title='Edit Player'
        onBack={handleRequestClose}
        actions={
          <>
            <HeaderActionButton
              text='Cancel'
              onClick={handleRequestClose}
              variant='secondary'
              textWeight='medium'
              disabled={isLoading}
              icon={<HeaderCancelIcon />}
            />
            <HeaderActionButton
              text='Save'
              onClick={handleSave}
              variant='primary'
              textWeight='bold'
              disabled={isLoading}
              isLoading={isLoading}
              icon={<HeaderSaveIcon />}
            />
          </>
        }
      />

      {isLoading ? (
        <PlayerFormSkeleton stacked={isMobile} />
      ) : (
        <ScrollSpyLayout items={PLAYER_SETUP_ITEMS} stacked={isMobile} gap={24}>
          <ScrollSpyLayout.Sidebar
            width={256}
            ariaLabel='Player form categories'
          />
          <ScrollSpyLayout.Content
            ariaLabel='Player profile sections'
            paddingRight={16}
          >
            {/* Email and name are locked here — this modal only ever edits an
                existing player, and they identify the player. */}
            <PersonalSection
              // Display-only `*` markers — validation stays the API's job.
              requiredNameFields
              requireEmail
              lockEmail
              lockNames
              // Canlan-only; renders null when the profile has no sf_id.
              extraIdChips={<SalesforceIdChip salesforceId={salesforceId} />}
            />
            <AddressSection />
            <EmergencyContactSection />
            <SocialProfilesSection />
            <MembershipSection />
          </ScrollSpyLayout.Content>
        </ScrollSpyLayout>
      )}

      <ConfirmDiscardChangesModal
        open={isConfirmDiscardOpen}
        onCancel={() => setIsConfirmDiscardOpen(false)}
        onConfirm={onCancel}
      />
    </>
  )
}

export default EditPlayerModalContent
